<script lang="ts" setup>
import { ref, useTemplateRef, watch } from 'vue'

import {
  MultiSelect,
  MultiSelectValue,
  SuccessBadge,
} from '@collector/shared-ui'
import { SubParticipantsPatchPayload } from '@collector/sportsapi-client'
import { BaseballSubParticipantStatId } from '@collector/sportsapi-client-legacy'
import { SubParticipant } from '@collector/sportsapi-types'
import { sportsApiClient } from '@desktop/globalState/sportsApiClient'
import { useEvent } from '@desktop/views/Relation/composables'
import {
  IncidentPlayerOption,
  IncidentPlayerOptionValue,
} from '@desktop/views/Relation/types'

interface Props {
  label: string
  options: IncidentPlayerOption[]
  statId: BaseballSubParticipantStatId
  value: SubParticipant | null
}

const props = defineProps<Props>()

// composables
const { event } = useEvent()
const successBadge = useTemplateRef('successBadge')

// state
const modelValue = ref<MultiSelectValue>()

// methods
function getUpdatePlayerPayload(
  playerOption: IncidentPlayerOptionValue,
  statId: BaseballSubParticipantStatId,
  statValue: string,
): SubParticipantsPatchPayload {
  return {
    short_name: playerOption.shortName as string,
    id: playerOption.id,
    team_id: playerOption.teamId,
    lineup_id: playerOption.lineupId as number,
    stats: [
      {
        id: statId,
        value: statValue,
      },
    ],
  }
}

async function onPlayerChange(value: MultiSelectValue): Promise<void> {
  const payloads: SubParticipantsPatchPayload[] = []

  if (modelValue.value) {
    // update stat to 0 for current player
    const currentValuePayload = getUpdatePlayerPayload(
      modelValue.value as IncidentPlayerOptionValue,
      props.statId,
      '0',
    )
    payloads.push(currentValuePayload)
  }

  if (value) {
    // update stat to 1 for new player
    const newValuePayload = getUpdatePlayerPayload(
      value as IncidentPlayerOptionValue,
      props.statId,
      '1',
    )

    payloads.push(newValuePayload)
  }

  modelValue.value = value

  await sportsApiClient.Events.SubParticipants.patch(
    event.value.id,
    payloads,
    false,
  )

  successBadge.value?.show()
}

watch(
  () => props.value,
  (newValue) => {
    modelValue.value = newValue
      ? {
          shortName: newValue.short_name,
          id: newValue.id ?? null,
          teamId: newValue.team_id,
          lineupId: newValue.lineup_id ?? null,
        }
      : undefined
  },
  {
    immediate: true,
  },
)
</script>

<template>
  <MultiSelect
    required
    :modelValue
    :options
    withFilter
    withClear="option"
    @update:modelValue="onPlayerChange"
  >
    <span class="w-12">
      {{ label }}
    </span>
    <template #trigger>
      <SuccessBadge
        ref="successBadge"
        :title="`${label} updated`"
      />
    </template>
  </MultiSelect>
</template>
